import { styleDefObj } from "@/pages/Chat/components/style-customize";
import { MimeType } from "@/types";
import {
  checkImageFromMimeType,
  checkVideoFromMimeType,
  cn,
  getExtensionFromUrl,
  getMimeTypeFromUrl,
} from "@/utils";
import { ReactNode, useMemo } from "react";
import { DefaultExtensionType, FileIcon, defaultStyles } from "react-file-icon";
import { BsPlayCircleFill } from "react-icons/bs";

interface CommonProps {
  className?: string;
  mimeType?: string;
  name?: string;
  render?: ({
    mimeType,
    className,
    url,
  }: {
    mimeType: MimeType;
    className?: string;
    url: string;
  }) => ReactNode;
  onClick?: () => void;
  documentType?: "video" | "image" | "document";
  classNameByDocumentType?: (
    documentType: "video" | "image" | "document"
  ) => string;
}

interface FileProps extends CommonProps {
  file: File;
}

export const AttachmentFileIcon = ({ file, ...remainProps }: FileProps) => {
  const url = useMemo(() => {
    return URL.createObjectURL(file);
  }, [file]);

  return <Icon {...remainProps} mimeType={file.type} url={url} />;
};

interface UrlProps extends CommonProps {
  url: string;
  mimeType?: string;
}

export const AttachmentUrlIcon = ({
  url,
  mimeType,
  ...remainProps
}: UrlProps) => {
  const _mimeType = useMemo(() => {
    return mimeType ? mimeType : getMimeTypeFromUrl(url);
  }, [url, mimeType]);
  return <Icon {...remainProps} url={url} mimeType={_mimeType} />;
};

const Icon = ({
  mimeType,
  className,
  url,
  render,
  name,
  onClick,
  documentType,
  classNameByDocumentType,
}: CommonProps & { mimeType: string; url: string }) => {
  if (render) {
    const customRender = render({
      mimeType: mimeType as MimeType,
      className,
      url,
    });
    if (customRender) return <>{customRender}</>;
  }

  if (
    (documentType && documentType === "image") ||
    checkImageFromMimeType(mimeType)
  )
    return (
      <img
        src={url}
        alt={name}
        className={cn(
          "object-cover rounded-lg",
          className,
          classNameByDocumentType ? classNameByDocumentType("image") : ""
        )}
        onClick={onClick}
      />
    );

  if (
    (documentType && documentType === "video") ||
    checkVideoFromMimeType(mimeType)
  )
    return (
      <div className="relative cursor-pointer" onClick={onClick}>
        <video
          src={url}
          className={cn(
            "object-cover rounded-lg cursor-pointer",
            className,
            classNameByDocumentType ? classNameByDocumentType("video") : ""
          )}
        >
          ビデオの読み込みに失敗しました。
        </video>

        <div className="bg-light_primary/40 inset-0 absolute flex items-center justify-center rounded-lg">
          <BsPlayCircleFill className="text-dark_primary size-8" />
        </div>
      </div>
    );

  const ext = getExtensionFromUrl(url) as DefaultExtensionType;

  const customDefaultLabelColor = styleDefObj[ext]
    ? styleDefObj[ext]["labelColor"] ?? "#777"
    : "#777";

  // Library defined default labelCOlor
  const libDefaultGlyphColor =
    defaultStyles[ext] && defaultStyles[ext]["labelColor"];

  return (
    <div
      className={cn(
        className,
        classNameByDocumentType ? classNameByDocumentType("document") : ""
      )}
      onClick={onClick}
    >
      <FileIcon
        extension={ext}
        glyphColor={libDefaultGlyphColor ?? customDefaultLabelColor}
        labelColor={customDefaultLabelColor}
        {...defaultStyles[ext]}
        {...styleDefObj[ext]}
      />
    </div>
  );
};
